import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

type LegalPageType = 'privacy' | 'terms' | 'company' | 'accessibility' | 'shipping' | 'withdrawal' | 'contact';

interface LegalPageProps {
  type: LegalPageType;
  onClose: () => void;
}

export function LegalPage({ type, onClose }: LegalPageProps) {
  const { t } = useTranslation();

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const getContent = () => {
    switch (type) {
      case 'privacy':
        return <PrivacyPolicyContent />;
      case 'terms':
        return <TermsOfServiceContent />;
      case 'company':
        return <CompanyInfoContent />;
      case 'accessibility':
        return <AccessibilityContent />;
      case 'shipping':
        return <ShippingContent />;
      case 'withdrawal':
        return <WithdrawalContent />;
      case 'contact':
        return <ContactContent />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    const titles: Record<LegalPageType, string> = {
      privacy: t('legal.privacy.title', { defaultValue: 'Privacy Policy' }),
      terms: t('legal.terms.title', { defaultValue: 'Terms of Service' }),
      company: t('legal.company.title', { defaultValue: 'About Us' }),
      accessibility: t('legal.accessibility.title', { defaultValue: 'Accessibility Statement' }),
      shipping: t('legal.shipping.title', { defaultValue: 'Shipping & Returns' }),
      withdrawal: t('legal.withdrawal.title', { defaultValue: 'Right of Withdrawal' }),
      contact: t('legal.contact.title', { defaultValue: 'Contact' }),
    };
    return titles[type];
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white-cream rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-wood-warm/30">
          <h1 className="text-2xl font-bold text-charcoal-dark">{getTitle()}</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-wood-warm/20 rounded-lg transition-colors"
            aria-label={t('common.close', { defaultValue: 'Close' })}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 prose prose-sm max-w-none">
          {getContent()}
        </div>
      </div>
    </div>
  );
}

function PrivacyPolicyContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <p className="text-sm text-charcoal-medium">
        {t('legal.privacy.lastUpdated', { defaultValue: 'Last updated: {{date}}', date: new Date().toLocaleDateString() })}
      </p>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section1.title', { defaultValue: '1. Data Controller' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section1.content', { 
            defaultValue: 'The data controller is Siella (Business ID: [Y-TUNNUS], Address: Tampere, Finland, Email: siellamapposters@gmail.com).'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section2.title', { defaultValue: '2. Personal Data We Collect' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section2.content', { 
            defaultValue: 'We collect the following personal data: name, email address, shipping address, payment information, and order history.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section3.title', { defaultValue: '3. Purpose of Processing' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section3.content', { 
            defaultValue: 'We process your personal data to fulfill orders, provide customer service, send order confirmations, and comply with legal obligations.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section4.title', { defaultValue: '4. Data Retention' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section4.content', { 
            defaultValue: 'We retain your personal data for as long as necessary to fulfill the purposes described in this policy, or as required by law. Order data is retained for 7 years as required by Finnish accounting law.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section5.title', { defaultValue: '5. Your Rights' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section5.content', { 
            defaultValue: 'You have the right to access, rectify, erase, restrict processing, object to processing, and data portability regarding your personal data. To exercise these rights, contact us at siellamapposters@gmail.com.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section6.title', { defaultValue: '6. Cookies' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section6.content', { 
            defaultValue: 'We use cookies to improve your experience on our website. You can control cookies through your browser settings.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.privacy.section7.title', { defaultValue: '7. Contact' })}
        </h2>
        <p className="mb-2">
          {t('legal.privacy.section7.content', { 
            defaultValue: 'For questions about this privacy policy, contact us at siellamapposters@gmail.com.'
          })}
        </p>
      </section>
    </div>
  );
}

function TermsOfServiceContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <p className="text-sm text-charcoal-medium">
        {t('legal.terms.lastUpdated', { defaultValue: 'Last updated: {{date}}', date: new Date().toLocaleDateString() })}
      </p>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.terms.section1.title', { defaultValue: '1. General' })}
        </h2>
        <p className="mb-2">
          {t('legal.terms.section1.content', { 
            defaultValue: 'These terms and conditions apply to all orders placed through our online store. By placing an order, you agree to these terms.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.terms.section2.title', { defaultValue: '2. Products and Prices' })}
        </h2>
        <p className="mb-2">
          {t('legal.terms.section2.content', { 
            defaultValue: 'All prices include VAT (24% in Finland). We reserve the right to change prices. The price at the time of order confirmation applies.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.terms.section3.title', { defaultValue: '3. Orders and Payment' })}
        </h2>
        <p className="mb-2">
          {t('legal.terms.section3.content', { 
            defaultValue: 'Orders are processed through Shopify. Payment methods include credit cards and other methods available through Shopify. The order is confirmed when payment is received.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.terms.section4.title', { defaultValue: '4. Delivery' })}
        </h2>
        <p className="mb-2">
          {t('legal.terms.section4.content', { 
            defaultValue: 'Delivery times are usually 5 business days from order confirmation. Delivery is handled by Posti. Shipping costs are shown at checkout.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.terms.section5.title', { defaultValue: '5. Right of Withdrawal' })}
        </h2>
        <p className="mb-2">
          {t('legal.terms.section5.content', { 
            defaultValue: 'You have the right to withdraw from the purchase within 14 days of receiving the product. See our withdrawal policy for details.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.terms.section6.title', { defaultValue: '6. Complaints' })}
        </h2>
        <p className="mb-2">
          {t('legal.terms.section6.content', { 
            defaultValue: 'If you have a complaint, contact us at siellamapposters@gmail.com. We will respond within 14 days.'
          })}
        </p>
      </section>
    </div>
  );
}

function CompanyInfoContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.company.section1.title', { defaultValue: 'About Siella' })}
        </h2>
        <p className="mb-2">
          {t('legal.company.section1.content', { 
            defaultValue: 'Siella creates personalized map posters. We print high-quality maps on premium paper in Tampere, Finland.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.company.section2.title', { defaultValue: 'Company Information' })}
        </h2>
        <ul className="space-y-2">
          <li>
            <strong>{t('legal.company.name', { defaultValue: 'Company Name' })}:</strong>{' '}
            {t('legal.company.nameValue', { defaultValue: 'Siella' })}
          </li>
          <li>
            <strong>{t('legal.company.businessId', { defaultValue: 'Business ID' })}:</strong>{' '}
            {t('legal.company.businessIdValue', { defaultValue: '[Y-TUNNUS]' })}
          </li>
          <li>
            <strong>{t('legal.company.address', { defaultValue: 'Address' })}:</strong>{' '}
            {t('legal.company.addressValue', { defaultValue: 'Tampere, Finland' })}
          </li>
          <li>
            <strong>{t('legal.company.email', { defaultValue: 'Email' })}:</strong>{' '}
            {t('legal.company.emailValue', { defaultValue: 'siellamapposters@gmail.com' })}
          </li>
        </ul>
      </section>
    </div>
  );
}

function AccessibilityContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.accessibility.section1.title', { defaultValue: 'Accessibility Statement' })}
        </h2>
        <p className="mb-2">
          {t('legal.accessibility.section1.content', { 
            defaultValue: 'We are committed to making our website accessible to all users. We strive to comply with WCAG 2.1 level AA standards.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.accessibility.section2.title', { defaultValue: 'Feedback' })}
        </h2>
        <p className="mb-2">
          {t('legal.accessibility.section2.content', { 
            defaultValue: 'If you encounter accessibility issues, please contact us at siellamapposters@gmail.com. We will respond within 14 days.'
          })}
        </p>
      </section>
    </div>
  );
}

function ShippingContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.shipping.section1.title', { defaultValue: 'Shipping' })}
        </h2>
        <p className="mb-2">
          {t('legal.shipping.section1.content', { 
            defaultValue: 'We ship from Tampere, Finland. Delivery times are usually 5 business days. Shipping costs are shown at checkout.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.shipping.section2.title', { defaultValue: 'Returns' })}
        </h2>
        <p className="mb-2">
          {t('legal.shipping.section2.content', { 
            defaultValue: 'You can return products within 14 days of receipt. Products must be in original condition. Return shipping costs are paid by the customer unless the product is defective.'
          })}
        </p>
      </section>
    </div>
  );
}

function WithdrawalContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.withdrawal.section1.title', { defaultValue: 'Right of Withdrawal' })}
        </h2>
        <p className="mb-2">
          {t('legal.withdrawal.section1.content', { 
            defaultValue: 'You have the right to withdraw from the purchase within 14 days of receiving the product without giving any reason.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.withdrawal.section2.title', { defaultValue: 'How to Withdraw' })}
        </h2>
        <p className="mb-2">
          {t('legal.withdrawal.section2.content', { 
            defaultValue: 'To withdraw, send an email to siellamapposters@gmail.com with your order number. You can also use the withdrawal form on our website.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.withdrawal.section3.title', { defaultValue: 'Return of Products' })}
        </h2>
        <p className="mb-2">
          {t('legal.withdrawal.section3.content', { 
            defaultValue: 'You must return the products within 14 days of notifying us of withdrawal. Products must be in original condition. Return shipping costs are paid by the customer.'
          })}
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.withdrawal.section4.title', { defaultValue: 'Refund' })}
        </h2>
        <p className="mb-2">
          {t('legal.withdrawal.section4.content', { 
            defaultValue: 'We will refund the purchase price within 14 days of receiving the returned products or proof of return shipment.'
          })}
        </p>
      </section>
    </div>
  );
}

function ContactContent() {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6 text-charcoal-dark">
      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.contact.section1.title', { defaultValue: 'Contact Us' })}
        </h2>
        <ul className="space-y-2">
          <li>
            <strong>{t('legal.contact.email', { defaultValue: 'Email' })}:</strong>{' '}
            <a href="mailto:siellamapposters@gmail.com" className="text-green-dark hover:underline">
              siellamapposters@gmail.com
            </a>
          </li>
          <li>
            <strong>{t('legal.contact.address', { defaultValue: 'Address' })}:</strong>{' '}
            {t('legal.contact.addressValue', { defaultValue: 'Tampere, Finland' })}
          </li>
          <li>
            <strong>{t('legal.contact.businessId', { defaultValue: 'Business ID' })}:</strong>{' '}
            {t('legal.contact.businessIdValue', { defaultValue: '[Y-TUNNUS]' })}
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">
          {t('legal.contact.section2.title', { defaultValue: 'Response Time' })}
        </h2>
        <p className="mb-2">
          {t('legal.contact.section2.content', { 
            defaultValue: 'We aim to respond to all inquiries within 2 business days.'
          })}
        </p>
      </section>
    </div>
  );
}
